/**
 * The buyer-plane routes behind Pay with Lissen, as the component needs
 * them.
 *
 * They are part of the generated ticketing client, and this module is the one
 * place the component reaches them: it narrows `createBuyerClient` to the four
 * calls Pay with Lissen makes and turns their failures into one error shape,
 * so no component code knows how the call is made.
 *
 *   POST /v1/buyer/checkout_sessions/{id}/identity      { action_token }
 *   POST /v1/buyer/checkout_sessions/{id}/enrolment     { email }
 *   POST /v1/buyer/checkout_sessions/{id}/wallet/setup  {}
 *   POST /v1/buyer/checkout_sessions/{id}/wallet/pay    { payment_method_id, keep,
 *                                                         confirm?, radar_session? }
 *
 * Every one is authorised by the session's own client secret as a bearer,
 * the same credential the rest of the buyer plane takes. Nothing here needs a
 * DOM: a React Native renderer makes the same calls through the same port.
 */

import { createBuyerClient } from "@libs/ticketing-client";
import type { components } from "@libs/ticketing-client";

// The wire shapes are the generated contract's, so a change to a buyer-plane
// response fails compilation here instead of reaching the machine at runtime.
type Wire = components["schemas"];
export type SavedCard = Wire["BuyerSavedCard"];
type BuyerIdentityWire = Wire["BuyerIdentity"];
type BuyerEnrolmentWire = Wire["BuyerEnrolment"];

/** Who the engine believes is buying. The engine's answer is always an
 *  attested one (a refused token is an error, never an unattested 200); the
 *  component also holds the not-yet-attested state in this shape, hence the
 *  wider `attested` and `email`. */
export type BuyerIdentity = Omit<BuyerIdentityWire, "attested" | "email"> & {
  attested: boolean;
  email: string | null;
};

/** A platform SetupIntent for entering a new card into the Lissen wallet. */
export type WalletSetup = Wire["BuyerWalletSetup"];

/** A wallet method cloned onto the organizer's account and attached to the
 *  session's PaymentIntent, ready to confirm on the connected instance. */
export type BuyerWalletPayment = Wire["BuyerWalletPayment"];

/** What the pay call is asked. */
export type WalletPayBody = {
  payment_method_id: string;
  keep: boolean;
  confirm?: "server";
  radar_session?: string;
};

export type WalletApiError = {
  status: number;
  code: string | null;
  message: string;
  /** The envelope's `detail`, kept for refusals whose message depends on it
   *  (the event checkout limits say which limit and what is left). */
  detail?: unknown;
};

export class WalletApiFailure extends Error {
  constructor(readonly failure: WalletApiError) {
    super(failure.message);
    this.name = "WalletApiFailure";
  }
}

/** The engine refused because the attestation is stale; a code and a retry
 *  is the answer, not an apology. */
export const isReverifyRequired = (error: unknown) =>
  error instanceof WalletApiFailure &&
  (error.failure.code === "reverify_required" ||
    (error.failure.status === 403 &&
      error.failure.message.includes("reverify")));

export type WalletApi = {
  /** Attest the buyer on this session with a verified action token. */
  identity: (
    sessionId: string,
    body: { action_token: string },
  ) => Promise<BuyerIdentity>;
  /** Withdraw the attestation from this session: the buyer took the chip
   *  off or signed out, and the engine must stop treating them as that
   *  buyer — an attestation that outlived the sign-in would decide whose
   *  wallet the next card lands in. */
  clearIdentity: (sessionId: string) => Promise<{ attested: false }>;
  /** Whether this address is already a Lissen buyer, without saying more. */
  enrolment: (
    sessionId: string,
    body: { email: string },
  ) => Promise<{ enrolled: boolean }>;
  /** Begin a new card: a SetupIntent on the platform account (never the
   *  organizer's), whether or not the buyer chose to keep it. */
  walletSetup: (sessionId: string) => Promise<WalletSetup>;
  /** Clone a wallet method onto the organizer's account and point this
   *  session's PaymentIntent at it. `keep: false` is released afterwards.
   *  With `confirm: "server"` the engine confirms the payment too, with the
   *  device's `radar_session`, and says how it went
   *  (`payment_intent_status`); a card that refuses is a 422
   *  `payment_declined` in the card's own words. */
  walletPay: (
    sessionId: string,
    body: WalletPayBody,
  ) => Promise<BuyerWalletPayment>;
};

type WalletCall<T> = Promise<{
  data?: T;
  error?: unknown;
  response: Response;
}>;

function failureOf(error: unknown, status: number): WalletApiError {
  const envelope = (
    error as {
      error?: {
        code?: unknown;
        type?: unknown;
        message?: unknown;
        detail?: unknown;
      };
    } | null
  )?.error;
  return {
    status,
    code:
      typeof envelope?.code === "string"
        ? envelope.code
        : typeof envelope?.type === "string"
          ? envelope.type
          : null,
    message:
      typeof envelope?.message === "string" && envelope.message
        ? envelope.message
        : `The request failed (${status}).`,
    ...(envelope?.detail === undefined ? {} : { detail: envelope.detail }),
  };
}

/**
 * The buyer plane through the generated client (LIS-685), so the component
 * and the engine share one contract. A rejected fetch (offline, CORS, DNS)
 * is a failure with status 0; a non-2xx answer carries the engine's error
 * envelope.
 */
export function createWalletApi(options: {
  baseUrl: string;
  clientSecret: string;
  fetch?: (request: Request) => Promise<Response>;
}): WalletApi {
  const buyer = createBuyerClient(options);
  const unwrap = async <T>(call: () => WalletCall<T>): Promise<T> => {
    let result: Awaited<WalletCall<T>>;
    try {
      result = await call();
    } catch {
      throw new WalletApiFailure({
        status: 0,
        code: "unreachable",
        message: "We couldn't reach the box office. Check your connection.",
      });
    }
    if (result.data !== undefined) return result.data;
    throw new WalletApiFailure(failureOf(result.error, result.response.status));
  };
  return {
    identity: (id, body) =>
      unwrap(() => buyer.identity(id, body) as WalletCall<BuyerIdentityWire>),
    clearIdentity: (id) =>
      unwrap(() => buyer.clearIdentity(id) as WalletCall<{ attested: false }>),
    enrolment: (id, body) =>
      unwrap(() => buyer.enrolment(id, body) as WalletCall<BuyerEnrolmentWire>),
    walletSetup: (id) =>
      unwrap(() => buyer.walletSetup(id) as WalletCall<WalletSetup>),
    walletPay: (id, body) =>
      unwrap(() => buyer.walletPay(id, body) as WalletCall<BuyerWalletPayment>),
  };
}
